import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { authFetch } from '../../utils/authFetch';

// Import legacy action types for extraReducers bridge
import {
  ADD_MANUAL_RISK,
  MOVE_RISK_TO_SAVED,
  UPDATE_SAVED_RISK,
  DISCARD_RISKS,
  DISCARD_UNSAVED_RISKS,
  DISCARD_SAVED_RISK,
  UPDATE_RISK_NAME,
  UPDATE_RISK_PRIORITY,
  FETCH_ASSESSMENT_RISKS_SUCCESS,
  MOVE_SCENARIO_TO_RISKS,
  REFINE_RISK_START,
  REFINE_RISK_SUCCESS,
  REFINE_RISK_FAILURE,
  SET_RISK_PRIORITY_START,
  SET_RISK_PRIORITY_SUCCESS,
  SET_RISK_PRIORITY_FAILURE,
} from '../actions/actionTypes.js';

// ============================================================================
// Risks Slice
// Manages core risk CRUD operations for assessments
// ============================================================================

// ============================================================================
// Async Thunks - API calls
// ============================================================================

/**
 * Fetch saved risks from database for an assessment
 * Used by autopilot to load existing risks on resume
 *
 * IMPORTANT: This thunk dispatches BOTH the RTK action AND the legacy action
 * to ensure risks are populated in both state.riskItems (RTK) and state.risks (legacy).
 * Many components still read from state.risks (prioritizeRisks, ResidualRiskAutopilot, etc.)
 */
export const fetchAssessmentRisks = createAsyncThunk(
  'riskItems/fetchAssessmentRisks',
  async ({ assessmentUuid, modelId }, { dispatch, rejectWithValue, getState }) => {
    console.log('[fetchAssessmentRisks] Starting fetch for', { assessmentUuid, modelId });
    try {
      const countRisks = (modelRisks = {}) =>
        Object.values(modelRisks).reduce((sum, area) => {
          const areaRisks = area?.areaRisks || {};
          return sum + Object.values(areaRisks).reduce((c, arr) => c + (Array.isArray(arr) ? arr.length : 0), 0);
        }, 0);

      const preRiskItems = getState().riskItems?.byAssessment?.[assessmentUuid]?.modelRisks || {};
      const preRiskItemsCount = countRisks(preRiskItems);
      console.log('[fetchAssessmentRisks] Pre-fetch count:', preRiskItemsCount);

      const response = await authFetch('/.netlify/functions/fetchModelRisks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid }),
      });

      console.log('[fetchAssessmentRisks] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch assessment risks`);
      }

      const data = await response.json();
      const risks = data.data || [];
      console.log('[fetchAssessmentRisks] Risks fetched from API:', risks.length, 'risks');

      // Also dispatch the legacy action so state.risks gets populated
      // This ensures components reading from state.risks.byAssessment work correctly
      dispatch({
        type: FETCH_ASSESSMENT_RISKS_SUCCESS,
        payload: { assessmentUuid, risks }
      });

      const legacyCount = countRisks(getState().risks?.byAssessment?.[assessmentUuid]?.modelRisks || {});
      console.info('[fetchAssessmentRisks] Canonical=RTK riskItems. Pre-count:', preRiskItemsCount, 'API payload length:', risks.length, 'legacy mirror count:', legacyCount);

      return { assessmentUuid, modelId, risks };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch Explorer risks from database for an assessment
 * Used by ExplorerAssessmentCanvas to load existing risks on mount
 */
export const fetchExplorerRisks = createAsyncThunk(
  'riskItems/fetchExplorerRisks',
  async ({ assessmentUuid, modelId }, { rejectWithValue }) => {
    console.log('[fetchExplorerRisks RTK] Fetching for assessment:', assessmentUuid);
    try {
      const response = await authFetch('/.netlify/functions/fetchModelRisks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          assessmentType: 'explorer'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch Explorer risks`);
      }

      const data = await response.json();
      const risks = data.data || [];
      console.log('[fetchExplorerRisks RTK] Fetched', risks.length, 'Explorer risks');

      return { assessmentUuid, modelId, risks };
    } catch (error) {
      console.error('[fetchExplorerRisks RTK] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch Builder risks from database for an assessment
 * Used by BuilderAssessmentFlow to load existing risks on mount
 */
export const fetchBuilderRisks = createAsyncThunk(
  'riskItems/fetchBuilderRisks',
  async ({ assessmentUuid, modelId }, { rejectWithValue }) => {
    console.log('[fetchBuilderRisks RTK] Fetching for assessment:', assessmentUuid);
    try {
      const response = await authFetch('/.netlify/functions/fetchModelRisks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          assessmentType: 'navigator'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch Builder risks`);
      }

      const data = await response.json();
      const risks = data.data || [];
      console.log('[fetchBuilderRisks RTK] Fetched', risks.length, 'Builder risks');

      return { assessmentUuid, modelId, risks };
    } catch (error) {
      console.error('[fetchBuilderRisks RTK] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist risk name to database
 * Used in review mode for inline risk name editing
 */
export const persistRiskName = createAsyncThunk(
  'riskItems/persistRiskName',
  async ({ modelId, assessmentUuid, riskId, riskName, sectionName, category }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riskId,
          name: riskName,
          section: sectionName,
          category,
          modelUuid: modelId,
          assessmentUuid,
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update risk name: ${response.status}`);
      }

      return { assessmentUuid, sectionName, category, riskId, riskName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist risk evaluation rationale to database
 * Used in review mode for inline rationale editing
 */
export const persistRiskRationale = createAsyncThunk(
  'riskItems/persistRiskRationale',
  async ({ modelId, assessmentUuid, riskId, riskName, category, rationale }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveRiskEvaluation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid: modelId,
          assessmentUuid,
          riskId,
          riskName,
          category,
          rationale,
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update rationale: ${response.status}`);
      }

      return { assessmentUuid, riskId, rationale };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist control name to database
 * Used in review mode for inline control name editing
 */
export const persistControlName = createAsyncThunk(
  'controls/persistControlName',
  async ({ modelId, assessmentUuid, controlId, controlName, riskIds }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveControl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid: modelId,
          assessmentUuid,
          controlUuid: controlId,
          controlName,
          riskIds,
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update control name: ${response.status}`);
      }

      return { controlId, controlName, riskIds };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist risk priority to database
 * Used in review mode for inline priority editing
 */
export const persistRiskPriority = createAsyncThunk(
  'riskItems/persistRiskPriority',
  async ({ modelId, assessmentUuid, riskId, riskName, sectionName, category, priority, rationale }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveRiskPriority', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid: modelId,
          assessmentUuid,
          riskId,
          riskName,
          section: sectionName,
          category,
          priority,
          rationale: rationale || '',
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update priority: ${response.status}`);
      }

      return { assessmentUuid, sectionName, category, riskId, priority };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * State Shape:
 * {
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [sectionName]: {
 *         areaRisks: {
 *           [category]: Risk[]
 *         }
 *       }
 *     }
 *   },
 *   loading: boolean,
 *   error: string | null,
 *   refinedRiskDraft: { risk: string, rationale: string } | null
 * }
 *
 * Risk object shape:
 * {
 *   riskId: string,
 *   riskName: string,
 *   category: string,
 *   status: 'Draft' | 'Generated' | 'Saved' | 'Discarded',
 *   type: 'Manual' | 'Generated' | 'Scenario' | 'RAG',
 *   priority?: string,
 *   rationale?: string,
 *   owner?: { name, email, picture },
 *   refinedRisk?: string,
 *   refineRationale?: string,
 *   rag_source_file?: string,
 *   loading?: boolean,
 *   error?: string
 * }
 */

const initialState = {
  byAssessment: {},
  loading: false,
  error: null,
  refinedRiskDraft: null,
};

// Explorer risk areas (7 organizational areas)
export const EXPLORER_RISK_AREAS = [
  'commercialRisk',
  'operationalRisk',
  'complianceRisk',
  'societalRisk',
  'environmentalRisk',
  'reputationalRisk',
  'securityImpact'
];

// Builder risk areas (7 use-case-focused areas)
export const BUILDER_RISK_AREAS = [
  'userSafety',
  'functionalPerformance',
  'fairnessAndEquity',
  'dataAndPrivacy',
  'reliabilityAndContinuity',
  'humanOversightAndControl',
  'securityAndMisuse'
];

// Helper to ensure nested structure exists
// NOTE: Uses modelRisks wrapper to match legacy riskReducer structure
// This is required for selectors like findRiskById to work correctly
const ensureStructure = (state, assessmentUuid, sectionName, category) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = { modelRisks: {} };
  }
  if (!state.byAssessment[assessmentUuid].modelRisks) {
    state.byAssessment[assessmentUuid].modelRisks = {};
  }
  if (!state.byAssessment[assessmentUuid].modelRisks[sectionName]) {
    state.byAssessment[assessmentUuid].modelRisks[sectionName] = { areaRisks: {} };
  }
  if (!state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks) {
    state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks = {};
  }
  if (category && !state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks[category]) {
    state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks[category] = [];
  }
};

// Helper to ensure Explorer nested structure exists
// Explorer risks are stored in explorerRisks, separate from modelRisks (Builder)
const ensureExplorerStructure = (state, assessmentUuid, areaKey, category) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, assessmentType: null };
  }
  if (!state.byAssessment[assessmentUuid].explorerRisks) {
    state.byAssessment[assessmentUuid].explorerRisks = {};
  }
  if (!state.byAssessment[assessmentUuid].explorerRisks[areaKey]) {
    state.byAssessment[assessmentUuid].explorerRisks[areaKey] = {
      areaRisks: {},
      areaStatus: 'pending',
      areaAnalysis: null
    };
  }
  if (category && !state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks[category]) {
    state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks[category] = [];
  }
};

// Helper to ensure Builder nested structure exists
// Builder risks are stored in builderRisks, separate from modelRisks and explorerRisks
const ensureBuilderStructure = (state, assessmentUuid, areaKey, category) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, builderRisks: {}, assessmentType: null };
  }
  if (!state.byAssessment[assessmentUuid].builderRisks) {
    state.byAssessment[assessmentUuid].builderRisks = {};
  }
  if (!state.byAssessment[assessmentUuid].builderRisks[areaKey]) {
    state.byAssessment[assessmentUuid].builderRisks[areaKey] = {
      areaRisks: {},
      areaStatus: 'pending',
      areaAnalysis: null
    };
  }
  if (category && !state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks[category]) {
    state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks[category] = [];
  }
};

const risksSlice = createSlice({
  name: 'riskItems',
  initialState,
  reducers: {
    // RTK action creators for new code
    addManualRisk: (state, action) => {
      const { assessmentUuid, sectionName, category, riskName } = action.payload;
      ensureStructure(state, assessmentUuid, sectionName, category);

      const newRisk = {
        riskId: uuidv4(),
        riskName,
        category,
        status: 'Draft',
        type: 'Manual',
        owner: null,
      };

      state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks[category].push(newRisk);
    },

    moveRiskToSaved: (state, action) => {
      const { assessmentUuid, sectionName, risk } = action.payload;
      ensureStructure(state, assessmentUuid, sectionName, risk.category);

      const areaRisks = state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks;
      const cat = risk.category;

      // Filter out existing risk with same ID and add as saved
      areaRisks[cat] = [
        ...areaRisks[cat].filter((r) => r.riskId !== risk.riskId),
        { ...risk, status: 'Saved' },
      ];
    },

    updateSavedRisk: (state, action) => {
      const { assessmentUuid, sectionName, riskId, category, newDetails } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex] = {
          ...areaRisks[category][riskIndex],
          ...newDetails,
        };
      }
    },

    discardRisk: (state, action) => {
      const { assessmentUuid, sectionName, risk } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      // Mark risk as discarded across all categories
      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskName === risk.riskName ? { ...r, status: 'Discarded' } : r
        );
      });
    },

    discardSavedRisk: (state, action) => {
      const { assessmentUuid, sectionName, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskId === riskId ? { ...r, status: 'Discarded' } : r
        );
      });
    },

    updateRiskName: (state, action) => {
      const { assessmentUuid, sectionName, originalName, newName } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskName === originalName ? { ...r, riskName: newName } : r
        );
      });
    },

    updateRiskPriority: (state, action) => {
      const { assessmentUuid, sectionName, category, riskId, priority, rationale } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex].priority = priority;
        if (rationale !== undefined) {
          areaRisks[category][riskIndex].rationale = rationale;
        }
      }
    },

    // Move a scenario to risks (from scenario generation)
    moveScenarioToRisks: (state, action) => {
      const { assessmentUuid, riskArea, sourceName, scenario, owner } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea, sourceName);

      const riskId = scenario.scenarioId || uuidv4();
      const newRisk = {
        riskId,
        riskName: scenario.scenarioText || scenario.variantText || '',
        category: sourceName,
        status: 'Saved',
        type: 'Scenario',
        rationale: scenario.rationale || '',
        owner: owner || null,
      };

      // Dedupe by riskId
      const areaRisks = state.byAssessment[assessmentUuid].modelRisks[riskArea].areaRisks;
      areaRisks[sourceName] = [
        ...areaRisks[sourceName].filter((r) => r.riskId !== riskId),
        newRisk,
      ];
    },

    // Clear all risks for an assessment
    clearAssessmentRisks: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear refined risk draft
    clearRefinedRiskDraft: (state) => {
      state.refinedRiskDraft = null;
    },

    // ============================================================================
    // Explorer-Specific Reducers
    // ============================================================================

    // Initialize Explorer assessment structure with all 6 areas
    initializeExplorerAssessment: (state, action) => {
      const { assessmentUuid, areas = EXPLORER_RISK_AREAS } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, assessmentType: null };
      }
      state.byAssessment[assessmentUuid].explorerRisks = areas.reduce((acc, area) => {
        acc[area] = { areaRisks: {}, areaStatus: 'pending', areaAnalysis: null };
        return acc;
      }, {});
      state.byAssessment[assessmentUuid].assessmentType = 'explorer';
    },

    // Add Explorer risk to a specific area
    addExplorerRisk: (state, action) => {
      const { assessmentUuid, areaKey, category, risk } = action.payload;
      ensureExplorerStructure(state, assessmentUuid, areaKey, category);

      const riskId = risk.riskId || uuidv4();
      const newRisk = {
        ...risk,
        riskId,
        category,
        status: risk.status || 'Saved',
        type: risk.type || 'Explorer',
      };

      const areaRisks = state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks;
      // Dedupe by riskId
      areaRisks[category] = [
        ...areaRisks[category].filter((r) => r.riskId !== riskId),
        newRisk,
      ];
    },

    // Add multiple Explorer risks at once (batch operation)
    addExplorerRisksBatch: (state, action) => {
      const { assessmentUuid, areaKey, risks } = action.payload;
      risks.forEach((riskItem) => {
        const { category, risk } = riskItem;
        ensureExplorerStructure(state, assessmentUuid, areaKey, category);

        const riskId = risk.riskId || uuidv4();
        const newRisk = {
          ...risk,
          riskId,
          category,
          status: risk.status || 'Saved',
          type: risk.type || 'Explorer',
        };

        const areaRisks = state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks;
        areaRisks[category] = [
          ...areaRisks[category].filter((r) => r.riskId !== riskId),
          newRisk,
        ];
      });
    },

    // Update Explorer area status
    updateExplorerAreaStatus: (state, action) => {
      const { assessmentUuid, areaKey, status } = action.payload;
      if (state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]) {
        state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaStatus = status;
      }
    },

    // Set Explorer area analysis
    setExplorerAreaAnalysis: (state, action) => {
      const { assessmentUuid, areaKey, analysis } = action.payload;
      if (state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]) {
        state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaAnalysis = analysis;
      }
    },

    // Update Explorer risk details
    updateExplorerRisk: (state, action) => {
      const { assessmentUuid, areaKey, category, riskId, updates } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex] = {
          ...areaRisks[category][riskIndex],
          ...updates,
        };
      }
    },

    // Update Explorer risk priority
    updateExplorerRiskPriority: (state, action) => {
      const { assessmentUuid, areaKey, category, riskId, priority, rationale, impactFactors } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex].priority = priority;
        if (rationale !== undefined) {
          areaRisks[category][riskIndex].rationale = rationale;
        }
        if (impactFactors !== undefined) {
          areaRisks[category][riskIndex].impactFactors = impactFactors;
        }
      }
    },

    // Discard Explorer risk
    discardExplorerRisk: (state, action) => {
      const { assessmentUuid, areaKey, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskId === riskId ? { ...r, status: 'Discarded' } : r
        );
      });
    },

    // Delete Explorer risk (hard delete)
    deleteExplorerRisk: (state, action) => {
      const { assessmentUuid, areaKey, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].filter((r) => r.riskId !== riskId);
      });
    },

    // Clear all Explorer risks for an assessment
    clearExplorerRisks: (state, action) => {
      const { assessmentUuid } = action.payload;
      if (state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid].explorerRisks = {};
      }
    },

    // ============================================================================
    // Builder-Specific Reducers
    // ============================================================================

    // Initialize Builder assessment structure with all 6 areas
    initializeBuilderAssessment: (state, action) => {
      const { assessmentUuid, areas = BUILDER_RISK_AREAS } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, builderRisks: {}, assessmentType: null };
      }
      state.byAssessment[assessmentUuid].builderRisks = areas.reduce((acc, area) => {
        acc[area] = { areaRisks: {}, areaStatus: 'pending', areaAnalysis: null };
        return acc;
      }, {});
      state.byAssessment[assessmentUuid].assessmentType = 'navigator';
    },

    // Add Builder risk to a specific area
    addBuilderRisk: (state, action) => {
      const { assessmentUuid, areaKey, category, risk } = action.payload;
      ensureBuilderStructure(state, assessmentUuid, areaKey, category);

      const riskId = risk.riskId || uuidv4();
      const newRisk = {
        ...risk,
        riskId,
        category,
        status: risk.status || 'Saved',
        type: risk.type || 'Builder',
      };

      const areaRisks = state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks;
      areaRisks[category] = [
        ...areaRisks[category].filter((r) => r.riskId !== riskId),
        newRisk,
      ];
    },

    // Add multiple Builder risks at once (batch operation)
    addBuilderRisksBatch: (state, action) => {
      const { assessmentUuid, areaKey, risks } = action.payload;
      risks.forEach((riskItem) => {
        const { category, risk } = riskItem;
        ensureBuilderStructure(state, assessmentUuid, areaKey, category);

        const riskId = risk.riskId || uuidv4();
        const newRisk = {
          ...risk,
          riskId,
          category,
          status: risk.status || 'Saved',
          type: risk.type || 'Builder',
        };

        const areaRisks = state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks;
        areaRisks[category] = [
          ...areaRisks[category].filter((r) => r.riskId !== riskId),
          newRisk,
        ];
      });
    },

    // Update Builder area status
    updateBuilderAreaStatus: (state, action) => {
      const { assessmentUuid, areaKey, status } = action.payload;
      if (state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]) {
        state.byAssessment[assessmentUuid].builderRisks[areaKey].areaStatus = status;
      }
    },

    // Set Builder area analysis
    setBuilderAreaAnalysis: (state, action) => {
      const { assessmentUuid, areaKey, analysis } = action.payload;
      if (state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]) {
        state.byAssessment[assessmentUuid].builderRisks[areaKey].areaAnalysis = analysis;
      }
    },

    // Update Builder risk details
    updateBuilderRisk: (state, action) => {
      const { assessmentUuid, areaKey, category, riskId, updates } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex] = {
          ...areaRisks[category][riskIndex],
          ...updates,
        };
      }
    },

    // Update Builder risk priority
    updateBuilderRiskPriority: (state, action) => {
      const { assessmentUuid, areaKey, category, riskId, priority, rationale, impactFactors } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex].priority = priority;
        if (rationale !== undefined) {
          areaRisks[category][riskIndex].rationale = rationale;
        }
        if (impactFactors !== undefined) {
          areaRisks[category][riskIndex].impactFactors = impactFactors;
        }
      }
    },

    // Discard Builder risk
    discardBuilderRisk: (state, action) => {
      const { assessmentUuid, areaKey, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskId === riskId ? { ...r, status: 'Discarded' } : r
        );
      });
    },

    // Delete Builder risk (hard delete)
    deleteBuilderRisk: (state, action) => {
      const { assessmentUuid, areaKey, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].filter((r) => r.riskId !== riskId);
      });
    },

    // Clear all Builder risks for an assessment
    clearBuilderRisks: (state, action) => {
      const { assessmentUuid } = action.payload;
      if (state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid].builderRisks = {};
      }
    },

    // Set assessment type
    setAssessmentType: (state, action) => {
      const { assessmentUuid, assessmentType } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, assessmentType: null };
      }
      state.byAssessment[assessmentUuid].assessmentType = assessmentType;
    },
  },

  extraReducers: (builder) => {
    // ============================================================================
    // Legacy Action Type Bridge
    // ============================================================================

    builder.addCase(ADD_MANUAL_RISK, (state, action) => {
      const { assessmentUuid, sectionName, category, riskName } = action.payload;
      ensureStructure(state, assessmentUuid, sectionName, category);

      const newRisk = {
        riskId: uuidv4(),
        riskName,
        category,
        status: 'Draft',
        type: 'Manual',
        owner: null,
      };

      state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks[category].push(newRisk);
    });

    builder.addCase(MOVE_RISK_TO_SAVED, (state, action) => {
      const { assessmentUuid, sectionName, risk } = action.payload;
      ensureStructure(state, assessmentUuid, sectionName, risk.category);

      const areaRisks = state.byAssessment[assessmentUuid].modelRisks[sectionName].areaRisks;
      const cat = risk.category;

      areaRisks[cat] = [
        ...areaRisks[cat].filter((r) => r.riskId !== risk.riskId),
        { ...risk, status: 'Saved' },
      ];
    });

    builder.addCase(UPDATE_SAVED_RISK, (state, action) => {
      const { assessmentUuid, sectionName, riskId, category, newDetails } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex] = {
          ...areaRisks[category][riskIndex],
          ...newDetails,
        };
      }
    });

    builder.addCase(DISCARD_RISKS, (state, action) => {
      const { assessmentUuid, sectionName, risk } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskName === risk.riskName ? { ...r, status: 'Discarded' } : r
        );
      });
    });

    builder.addCase(DISCARD_UNSAVED_RISKS, (state, action) => {
      const { assessmentUuid, risksToDiscard } = action.payload;
      const modelRisks = state.byAssessment[assessmentUuid]?.modelRisks;
      if (!modelRisks) return;

      risksToDiscard.forEach((risk) => {
        Object.keys(modelRisks).forEach((sectionName) => {
          const areaRisks = modelRisks[sectionName]?.areaRisks;
          if (!areaRisks) return;

          Object.keys(areaRisks).forEach((category) => {
            areaRisks[category] = areaRisks[category].filter(
              (r) => !(r.riskName === risk.riskName && r.status === 'Draft')
            );
          });
        });
      });
    });

    builder.addCase(DISCARD_SAVED_RISK, (state, action) => {
      const { assessmentUuid, sectionName, riskId } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskId === riskId ? { ...r, status: 'Discarded' } : r
        );
      });
    });

    builder.addCase(UPDATE_RISK_NAME, (state, action) => {
      const { assessmentUuid, sectionName, originalName, newName } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks) return;

      Object.keys(areaRisks).forEach((category) => {
        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskName === originalName ? { ...r, riskName: newName } : r
        );
      });
    });

    builder.addCase(UPDATE_RISK_PRIORITY, (state, action) => {
      const { assessmentUuid, sectionName, category, riskId, priority } = action.payload;
      const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
      if (!areaRisks?.[category]) return;

      const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
      if (riskIndex !== -1) {
        areaRisks[category][riskIndex].priority = priority;
      }
    });

    builder.addCase(FETCH_ASSESSMENT_RISKS_SUCCESS, (state, action) => {
      const { assessmentUuid, risks } = action.payload;

      risks.forEach((risk) => {
        const section = risk.active_section;
        const category = risk.category || 'Uncategorized';
        ensureStructure(state, assessmentUuid, section, category);

        const owner = {
          name: risk.owner_name || 'Unknown',
          email: risk.owner_email || 'Unknown',
          picture: risk.owner_picture || 'https://via.placeholder.com/150',
        };

        const newRisk = {
          riskId: risk.risk_id,
          riskName: risk.risk_name,
          category: risk.category,
          priority: risk.priority,
          rationale: risk.rationale,
          status: 'Saved',
          owner,
          type: risk.risk_type || 'Unknown',
          rag_source_file: risk.rag_source_file,
          loading: false,
        };

        // Dedupe by riskId
        const areaRisks = state.byAssessment[assessmentUuid].modelRisks[section].areaRisks;
        const existingIndex = areaRisks[category].findIndex((r) => r.riskId === risk.risk_id);
        if (existingIndex !== -1) {
          areaRisks[category][existingIndex] = newRisk;
        } else {
          areaRisks[category].push(newRisk);
        }
      });
    });

    builder.addCase(MOVE_SCENARIO_TO_RISKS, (state, action) => {
      const { assessmentUuid, riskArea, sourceName, scenario, owner } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea, sourceName);

      const riskId = scenario.scenarioId || uuidv4();
      const newRisk = {
        riskId,
        riskName: scenario.scenarioText || scenario.variantText || '',
        category: sourceName,
        status: 'Saved',
        type: 'Scenario',
        rationale: scenario.rationale || '',
        owner: owner || null,
      };

      const areaRisks = state.byAssessment[assessmentUuid].modelRisks[riskArea].areaRisks;
      areaRisks[sourceName] = [
        ...areaRisks[sourceName].filter((r) => r.riskId !== riskId),
        newRisk,
      ];
    });

    // Risk priority setting with loading states
    builder
      .addCase(SET_RISK_PRIORITY_START, (state, action) => {
        const { assessmentUuid, sectionName, category, riskId } = action.payload;
        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) return;

        const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
        if (riskIndex !== -1) {
          areaRisks[category][riskIndex].loading = true;
        }
      })
      .addCase(SET_RISK_PRIORITY_SUCCESS, (state, action) => {
        const { assessmentUuid, sectionName, category, riskId, priority, rationale } = action.payload;
        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) return;

        const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
        if (riskIndex !== -1) {
          areaRisks[category][riskIndex].loading = false;
          areaRisks[category][riskIndex].priority = priority;
          areaRisks[category][riskIndex].rationale = rationale;
        }
      })
      .addCase(SET_RISK_PRIORITY_FAILURE, (state, action) => {
        const { assessmentUuid, sectionName, category, riskId, error } = action.payload;
        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) return;

        const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
        if (riskIndex !== -1) {
          areaRisks[category][riskIndex].loading = false;
          areaRisks[category][riskIndex].error = error;
        }
      });

    // Risk refinement
    builder
      .addCase(REFINE_RISK_START, (state, action) => {
        const { assessmentUuid } = action.payload;
        if (state.byAssessment[assessmentUuid]) {
          state.loading = true;
        }
      })
      .addCase(REFINE_RISK_SUCCESS, (state, action) => {
        const { assessmentUuid, sectionName, category, originalName, refinedRisk, rationale } =
          action.payload;
        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) {
          state.loading = false;
          return;
        }

        areaRisks[category] = areaRisks[category].map((r) =>
          r.riskName === originalName
            ? { ...r, refinedRisk, refineRationale: rationale }
            : r
        );

        state.loading = false;
        state.refinedRiskDraft = { risk: refinedRisk, rationale };
      })
      .addCase(REFINE_RISK_FAILURE, (state, action) => {
        const { error } = action.payload;
        state.loading = false;
        state.error = error;
      });

    // ============================================================================
    // RTK Async Thunk Handlers - fetchAssessmentRisks
    // ============================================================================
    builder
      .addCase(fetchAssessmentRisks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentRisks.fulfilled, (state, action) => {
        const { assessmentUuid, risks } = action.payload;
        state.loading = false;

        // Process each risk from the API response
        risks.forEach((risk) => {
          const section = risk.active_section;
          const category = risk.category || 'Uncategorized';
          ensureStructure(state, assessmentUuid, section, category);

          const owner = {
            name: risk.owner_name || 'Unknown',
            email: risk.owner_email || 'Unknown',
            picture: risk.owner_picture || 'https://via.placeholder.com/150',
          };

          const newRisk = {
            riskId: risk.risk_id,
            riskName: risk.risk_name,
            category: risk.category,
            priority: risk.priority,
            rationale: risk.rationale,
            status: 'Saved',
            owner,
            type: risk.risk_type || 'Unknown',
            rag_source_file: risk.rag_source_file,
            loading: false,
          };

          // Dedupe by riskId (using modelRisks wrapper)
          const areaRisks = state.byAssessment[assessmentUuid].modelRisks[section].areaRisks;
          const existingIndex = areaRisks[category].findIndex((r) => r.riskId === risk.risk_id);
          if (existingIndex !== -1) {
            areaRisks[category][existingIndex] = newRisk;
          } else {
            areaRisks[category].push(newRisk);
          }
        });
      })
      .addCase(fetchAssessmentRisks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch assessment risks';
      });

    // ============================================================================
    // fetchExplorerRisks handlers (RTK)
    // ============================================================================
    builder
      .addCase(fetchExplorerRisks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExplorerRisks.fulfilled, (state, action) => {
        const { assessmentUuid, risks } = action.payload;
        state.loading = false;

        // Initialize Explorer structure
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, assessmentType: null };
        }
        state.byAssessment[assessmentUuid].explorerRisks = EXPLORER_RISK_AREAS.reduce((acc, area) => {
          acc[area] = { areaRisks: {}, areaStatus: 'pending', areaAnalysis: null };
          return acc;
        }, {});
        state.byAssessment[assessmentUuid].assessmentType = 'explorer';

        // Process each risk from the API response
        risks.forEach((risk) => {
          const areaKey = risk.active_section || 'commercialRisk';
          const category = risk.category || 'Uncategorized';

          // Ensure structure exists
          if (!state.byAssessment[assessmentUuid].explorerRisks[areaKey]) {
            state.byAssessment[assessmentUuid].explorerRisks[areaKey] = {
              areaRisks: {},
              areaStatus: 'pending',
              areaAnalysis: null
            };
          }
          if (!state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks[category]) {
            state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks[category] = [];
          }

          const newRisk = {
            riskId: risk.risk_id,
            riskName: risk.risk_name,
            category: risk.category,
            priority: risk.priority,
            rationale: risk.rationale,
            status: 'Saved',
            type: risk.risk_type || 'Explorer',
            impactFactors: risk.impact_factors || [],
            owner: {
              name: risk.owner_name || 'Unknown',
              email: risk.owner_email || 'Unknown',
              picture: risk.owner_picture || 'https://via.placeholder.com/150'
            }
          };

          // Dedupe by riskId
          const areaRisks = state.byAssessment[assessmentUuid].explorerRisks[areaKey].areaRisks;
          const existingIndex = areaRisks[category].findIndex((r) => r.riskId === risk.risk_id);
          if (existingIndex !== -1) {
            areaRisks[category][existingIndex] = newRisk;
          } else {
            areaRisks[category].push(newRisk);
          }
        });
      })
      .addCase(fetchExplorerRisks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch Explorer risks';
      });

    // ============================================================================
    // fetchBuilderRisks handlers (RTK)
    // ============================================================================
    builder
      .addCase(fetchBuilderRisks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuilderRisks.fulfilled, (state, action) => {
        const { assessmentUuid, risks } = action.payload;
        state.loading = false;

        // Initialize Builder structure
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = { modelRisks: {}, explorerRisks: {}, builderRisks: {}, assessmentType: null };
        }
        state.byAssessment[assessmentUuid].builderRisks = BUILDER_RISK_AREAS.reduce((acc, area) => {
          acc[area] = { areaRisks: {}, areaStatus: 'pending', areaAnalysis: null };
          return acc;
        }, {});
        state.byAssessment[assessmentUuid].assessmentType = 'navigator';

        // Process each risk from the API response
        risks.forEach((risk) => {
          const areaKey = risk.active_section || 'userSafety';
          const category = risk.category || 'Uncategorized';

          // Ensure structure exists
          if (!state.byAssessment[assessmentUuid].builderRisks[areaKey]) {
            state.byAssessment[assessmentUuid].builderRisks[areaKey] = {
              areaRisks: {},
              areaStatus: 'pending',
              areaAnalysis: null
            };
          }
          if (!state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks[category]) {
            state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks[category] = [];
          }

          const newRisk = {
            riskId: risk.risk_id,
            riskName: risk.risk_name,
            category: risk.category,
            priority: risk.priority,
            rationale: risk.rationale,
            status: 'Saved',
            type: risk.risk_type || 'Builder',
            impactFactors: risk.impact_factors || [],
            owner: {
              name: risk.owner_name || 'Unknown',
              email: risk.owner_email || 'Unknown',
              picture: risk.owner_picture || 'https://via.placeholder.com/150'
            }
          };

          // Dedupe by riskId
          const areaRisks = state.byAssessment[assessmentUuid].builderRisks[areaKey].areaRisks;
          const existingIndex = areaRisks[category].findIndex((r) => r.riskId === risk.risk_id);
          if (existingIndex !== -1) {
            areaRisks[category][existingIndex] = newRisk;
          } else {
            areaRisks[category].push(newRisk);
          }
        });
      })
      .addCase(fetchBuilderRisks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch Builder risks';
      });

    // ============================================================================
    // persistRiskName handlers
    // ============================================================================
    builder
      .addCase(persistRiskName.pending, (state) => {
        state.loading = true;
      })
      .addCase(persistRiskName.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, sectionName, category, riskId, riskName } = action.payload;

        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) return;

        const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
        if (riskIndex !== -1) {
          areaRisks[category][riskIndex].riskName = riskName;
        }
      })
      .addCase(persistRiskName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[risksSlice] Failed to persist risk name:', action.payload);
      });

    // ============================================================================
    // persistRiskRationale handlers
    // ============================================================================
    builder
      .addCase(persistRiskRationale.pending, (state) => {
        state.loading = true;
      })
      .addCase(persistRiskRationale.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, riskId, rationale } = action.payload;

        // Update rationale across all sections/categories
        const modelRisks = state.byAssessment[assessmentUuid]?.modelRisks || {};
        Object.keys(modelRisks).forEach((sectionName) => {
          const areaRisks = modelRisks[sectionName]?.areaRisks || {};
          Object.keys(areaRisks).forEach((category) => {
            const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
            if (riskIndex !== -1) {
              areaRisks[category][riskIndex].rationale = rationale;
            }
          });
        });
      })
      .addCase(persistRiskRationale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[risksSlice] Failed to persist risk rationale:', action.payload);
      });

    // ============================================================================
    // persistRiskPriority handlers
    // ============================================================================
    builder
      .addCase(persistRiskPriority.pending, (state) => {
        state.loading = true;
      })
      .addCase(persistRiskPriority.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, sectionName, category, riskId, priority } = action.payload;

        const areaRisks = state.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks;
        if (!areaRisks?.[category]) return;

        const riskIndex = areaRisks[category].findIndex((r) => r.riskId === riskId);
        if (riskIndex !== -1) {
          areaRisks[category][riskIndex].priority = priority;
        }
      })
      .addCase(persistRiskPriority.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[risksSlice] Failed to persist risk priority:', action.payload);
      });
  },
});

export const {
  // Builder (existing) actions
  addManualRisk,
  moveRiskToSaved,
  updateSavedRisk,
  discardRisk,
  discardSavedRisk,
  updateRiskName,
  updateRiskPriority,
  moveScenarioToRisks,
  clearAssessmentRisks,
  setLoading,
  setError,
  clearRefinedRiskDraft,
  // Explorer-specific actions
  initializeExplorerAssessment,
  addExplorerRisk,
  addExplorerRisksBatch,
  updateExplorerAreaStatus,
  setExplorerAreaAnalysis,
  updateExplorerRisk,
  updateExplorerRiskPriority,
  discardExplorerRisk,
  deleteExplorerRisk,
  clearExplorerRisks,
  // Builder-specific actions
  initializeBuilderAssessment,
  addBuilderRisk,
  addBuilderRisksBatch,
  updateBuilderAreaStatus,
  setBuilderAreaAnalysis,
  updateBuilderRisk,
  updateBuilderRiskPriority,
  discardBuilderRisk,
  deleteBuilderRisk,
  clearBuilderRisks,
  setAssessmentType,
} = risksSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectRisksByAssessment = (state) => state.riskItems.byAssessment;

export const selectRisksForSection = (state, assessmentUuid, sectionName) =>
  state.riskItems.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks || {};

export const selectRisksForCategory = (state, assessmentUuid, sectionName, category) =>
  state.riskItems.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks?.[category] || [];

export const selectRiskById = (state, assessmentUuid, sectionName, riskId) => {
  const areaRisks =
    state.riskItems.byAssessment[assessmentUuid]?.modelRisks?.[sectionName]?.areaRisks || {};
  for (const category of Object.keys(areaRisks)) {
    const risk = areaRisks[category].find((r) => r.riskId === riskId);
    if (risk) return risk;
  }
  return null;
};

export const selectAllRisksForAssessment = (state, assessmentUuid) => {
  const modelRisks = state.riskItems.byAssessment[assessmentUuid]?.modelRisks || {};
  const allRisks = [];

  Object.keys(modelRisks).forEach((sectionName) => {
    const areaRisks = modelRisks[sectionName]?.areaRisks || {};
    Object.keys(areaRisks).forEach((category) => {
      allRisks.push(
        ...areaRisks[category].map((r) => ({
          ...r,
          sectionName,
        }))
      );
    });
  });

  return allRisks;
};

export const selectSavedRisksForAssessment = (state, assessmentUuid) =>
  selectAllRisksForAssessment(state, assessmentUuid).filter((r) => r.status === 'Saved');

export const selectRisksLoading = (state) => state.riskItems.loading;
export const selectRisksError = (state) => state.riskItems.error;
export const selectRefinedRiskDraft = (state) => state.riskItems.refinedRiskDraft;

/**
 * Count risks by status for an assessment
 */
export const selectRiskStatusCounts = (state, assessmentUuid) => {
  const allRisks = selectAllRisksForAssessment(state, assessmentUuid);
  return allRisks.reduce(
    (counts, risk) => {
      switch (risk.status) {
        case 'Saved':
          counts.saved++;
          break;
        case 'Draft':
          counts.draft++;
          break;
        case 'Generated':
          counts.generated++;
          break;
        case 'Discarded':
          counts.discarded++;
          break;
        default:
          break;
      }
      return counts;
    },
    { saved: 0, draft: 0, generated: 0, discarded: 0 }
  );
};

// ============================================================================
// Explorer-Specific Selectors
// ============================================================================

/**
 * Select the assessment type (builder | explorer | null)
 */
export const selectAssessmentType = (state, assessmentUuid) =>
  state.riskItems.byAssessment[assessmentUuid]?.assessmentType || null;

/**
 * Select all Explorer risks for an assessment (by area)
 */
export const selectExplorerRisks = (state, assessmentUuid) =>
  state.riskItems.byAssessment[assessmentUuid]?.explorerRisks || {};

/**
 * Select Explorer risks for a specific area
 */
export const selectExplorerAreaRisks = (state, assessmentUuid, areaKey) =>
  state.riskItems.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaRisks || {};

/**
 * Select Explorer area status
 */
export const selectExplorerAreaStatus = (state, assessmentUuid, areaKey) =>
  state.riskItems.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaStatus || 'pending';

/**
 * Select Explorer area analysis
 */
export const selectExplorerAreaAnalysis = (state, assessmentUuid, areaKey) =>
  state.riskItems.byAssessment[assessmentUuid]?.explorerRisks?.[areaKey]?.areaAnalysis || null;

/**
 * Select all Explorer risks flattened (for display)
 */
export const selectAllExplorerRisks = (state, assessmentUuid) => {
  const explorerRisks = state.riskItems.byAssessment[assessmentUuid]?.explorerRisks || {};
  const allRisks = [];

  Object.entries(explorerRisks).forEach(([areaKey, areaData]) => {
    const areaRisks = areaData?.areaRisks || {};
    Object.entries(areaRisks).forEach(([category, risks]) => {
      allRisks.push(
        ...risks.map((r) => ({
          ...r,
          areaKey,
          category,
        }))
      );
    });
  });

  return allRisks;
};

/**
 * Select saved Explorer risks only
 */
export const selectSavedExplorerRisks = (state, assessmentUuid) =>
  selectAllExplorerRisks(state, assessmentUuid).filter((r) => r.status === 'Saved');

/**
 * Select Explorer risks grouped by area key
 * Returns { areaKey: [risks] } structure for easy iteration
 */
export const selectExplorerRisksByArea = (state, assessmentUuid) => {
  const explorerRisks = state.riskItems.byAssessment[assessmentUuid]?.explorerRisks || {};
  const byArea = {};

  Object.entries(explorerRisks).forEach(([areaKey, areaData]) => {
    const areaRisks = areaData?.areaRisks || {};
    const flatRisks = [];

    Object.entries(areaRisks).forEach(([category, risks]) => {
      flatRisks.push(
        ...risks
          .filter((r) => r.status !== 'Discarded')
          .map((r) => ({
            ...r,
            areaKey,
            category,
          }))
      );
    });

    byArea[areaKey] = flatRisks;
  });

  return byArea;
};

/**
 * Select Builder risks grouped by area key
 * Returns { areaKey: [risks] } structure for easy iteration
 */
export const selectBuilderRisksByArea = (state, assessmentUuid) => {
  const builderRisks = state.riskItems.byAssessment[assessmentUuid]?.builderRisks || {};
  const byArea = {};

  Object.entries(builderRisks).forEach(([areaKey, areaData]) => {
    const areaRisks = areaData?.areaRisks || {};
    const flatRisks = [];

    Object.entries(areaRisks).forEach(([category, risks]) => {
      flatRisks.push(
        ...risks
          .filter((r) => r.status !== 'Discarded')
          .map((r) => ({
            ...r,
            areaKey,
            category,
          }))
      );
    });

    byArea[areaKey] = flatRisks;
  });

  return byArea;
};

/**
 * Select Explorer progress (count of completed areas)
 */
export const selectExplorerProgress = (state, assessmentUuid) => {
  const explorerRisks = state.riskItems.byAssessment[assessmentUuid]?.explorerRisks || {};
  const areas = Object.keys(explorerRisks);
  const completed = areas.filter((a) => explorerRisks[a]?.areaStatus === 'completed');
  return { total: areas.length, completed: completed.length };
};

/**
 * Select inherited Builder risks from parent assessment
 * Used by Explorer to show inherited technical risks
 */
export const selectInheritedBuilderRisks = (state, parentAssessmentUuid) => {
  if (!parentAssessmentUuid) return [];

  const parentData = state.riskItems.byAssessment[parentAssessmentUuid];
  if (!parentData?.modelRisks) return [];

  const allRisks = [];
  Object.entries(parentData.modelRisks).forEach(([sectionName, sectionData]) => {
    const areaRisks = sectionData?.areaRisks || {};
    Object.entries(areaRisks).forEach(([category, risks]) => {
      allRisks.push(
        ...risks.map((r) => ({
          ...r,
          sectionName,
          category,
        }))
      );
    });
  });

  return allRisks.filter((r) => r.status === 'Saved');
};

/**
 * Find an Explorer risk by ID across all areas
 */
export const selectExplorerRiskById = (state, assessmentUuid, riskId) => {
  const explorerRisks = state.riskItems.byAssessment[assessmentUuid]?.explorerRisks || {};

  for (const areaKey of Object.keys(explorerRisks)) {
    const areaRisks = explorerRisks[areaKey]?.areaRisks || {};
    for (const category of Object.keys(areaRisks)) {
      const risk = areaRisks[category].find((r) => r.riskId === riskId);
      if (risk) return { ...risk, areaKey, category };
    }
  }
  return null;
};

/**
 * Count Explorer risks by status
 */
export const selectExplorerRiskStatusCounts = (state, assessmentUuid) => {
  const allRisks = selectAllExplorerRisks(state, assessmentUuid);
  return allRisks.reduce(
    (counts, risk) => {
      switch (risk.status) {
        case 'Saved':
          counts.saved++;
          break;
        case 'Draft':
          counts.draft++;
          break;
        case 'Generated':
          counts.generated++;
          break;
        case 'Discarded':
          counts.discarded++;
          break;
        default:
          break;
      }
      return counts;
    },
    { saved: 0, draft: 0, generated: 0, discarded: 0 }
  );
};

// ============================================================================
// Builder-Specific Selectors
// ============================================================================

/**
 * Select all Builder risks for an assessment (by area)
 */
export const selectBuilderRisks = (state, assessmentUuid) =>
  state.riskItems.byAssessment[assessmentUuid]?.builderRisks || {};

/**
 * Select Builder risks for a specific area
 */
export const selectBuilderAreaRisks = (state, assessmentUuid, areaKey) =>
  state.riskItems.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaRisks || {};

/**
 * Select Builder area status
 */
export const selectBuilderAreaStatus = (state, assessmentUuid, areaKey) =>
  state.riskItems.byAssessment[assessmentUuid]?.builderRisks?.[areaKey]?.areaStatus || 'pending';

/**
 * Select all Builder risks flattened (for display)
 */
export const selectAllBuilderRisks = (state, assessmentUuid) => {
  const builderRisks = state.riskItems.byAssessment[assessmentUuid]?.builderRisks || {};
  const allRisks = [];

  Object.entries(builderRisks).forEach(([areaKey, areaData]) => {
    const areaRisks = areaData?.areaRisks || {};
    Object.entries(areaRisks).forEach(([category, risks]) => {
      allRisks.push(
        ...risks.map((r) => ({
          ...r,
          areaKey,
          category,
        }))
      );
    });
  });

  return allRisks;
};

/**
 * Select saved Builder risks only
 */
export const selectSavedBuilderRisks = (state, assessmentUuid) =>
  selectAllBuilderRisks(state, assessmentUuid).filter((r) => r.status === 'Saved');

/**
 * Select Builder progress (count of completed areas)
 */
export const selectBuilderProgress = (state, assessmentUuid) => {
  const builderRisks = state.riskItems.byAssessment[assessmentUuid]?.builderRisks || {};
  const areas = Object.keys(builderRisks);
  const completed = areas.filter((a) => builderRisks[a]?.areaStatus === 'completed');
  return { total: areas.length, completed: completed.length };
};

/**
 * Find a Builder risk by ID across all areas
 */
export const selectBuilderRiskById = (state, assessmentUuid, riskId) => {
  const builderRisks = state.riskItems.byAssessment[assessmentUuid]?.builderRisks || {};

  for (const areaKey of Object.keys(builderRisks)) {
    const areaRisks = builderRisks[areaKey]?.areaRisks || {};
    for (const category of Object.keys(areaRisks)) {
      const risk = areaRisks[category].find((r) => r.riskId === riskId);
      if (risk) return { ...risk, areaKey, category };
    }
  }
  return null;
};

/**
 * Count Builder risks by status
 */
export const selectBuilderRiskStatusCounts = (state, assessmentUuid) => {
  const allRisks = selectAllBuilderRisks(state, assessmentUuid);
  return allRisks.reduce(
    (counts, risk) => {
      switch (risk.status) {
        case 'Saved':
          counts.saved++;
          break;
        case 'Draft':
          counts.draft++;
          break;
        case 'Generated':
          counts.generated++;
          break;
        case 'Discarded':
          counts.discarded++;
          break;
        default:
          break;
      }
      return counts;
    },
    { saved: 0, draft: 0, generated: 0, discarded: 0 }
  );
};

export default risksSlice.reducer;
